import {
    type FeedingSourceState,
    WARNING_DIGITS,
    type WarningMetric,
} from '@/domain/feedingDashboard';
import type { Locale } from '@/utils/localeFormatters';

import { formatDashboardNumber } from '../dashboardNumber';

/** A cell's figure in the dashboard locale (U3), or "–" when there is none. */
export function format(value: number | null, digits: number, locale: Locale): string {
    if (value == null) return '–';
    return formatDashboardNumber(value, locale, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
}

/**
 * A target or margin as the reader configured it: up to `maxDigits` decimals, no padding,
 * so a target of 100 reads "100" rather than "100.0".
 */
export function formatSetting(value: number, maxDigits: number, locale: Locale): string {
    return formatDashboardNumber(value, locale, { maximumFractionDigits: maxDigits });
}

/**
 * A bFCR or RGI target as every surface names it, the cards, the table, the warning texts
 * and the chart keys alike: bFCR like its values ("1.10"), RGI as it was set ("100"), each
 * at the precision the warning rule compares it with.
 */
export function formatTarget(value: number, metric: WarningMetric, locale: Locale): string {
    const digits = WARNING_DIGITS[metric];
    return metric === 'fcr' ? format(value, digits, locale) : formatSetting(value, digits, locale);
}

/**
 * Marks a cell whose source this pen has no mapping for.
 *
 * A blank has two very different causes: the source is wired up but sent nothing,
 * or it was never wired up for this pen. Only the second is a setup task, and pens
 * on one site can differ mid-onboarding, so the distinction has to be per pen.
 * Returns a title so the blank is explained on hover rather than only in the chart
 * caption below.
 */
export function sourceTitle(
    state: FeedingSourceState,
    notConfiguredLabel: string
): string | undefined {
    return state === 'notConfigured' ? notConfiguredLabel : undefined;
}
